import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";
import { Recaptcha, RECAPTCHA_SITE_KEY } from "@/components/auth/Recaptcha";

function InviteCodeEntry({ t, applyInvite }) {
    const [show, setShow] = useState(false);
    const [value, setValue] = useState("");

    if (!show) {
        return (
            <button
                type="button"
                data-testid="login-have-invite-code"
                onClick={() => setShow(true)}
                className="text-[13.5px] text-[#F57C00] font-medium hover:underline"
            >
                {t("haveInviteCode")}
            </button>
        );
    }
    return (
        <div className="flex gap-2">
            <Input
                data-testid="login-invite-code-input"
                value={value}
                onChange={(e) => setValue(e.target.value.toUpperCase().slice(0, 10))}
                placeholder="e.g. VTABC234"
                className="h-11 bg-white border-[#E5E5E5] uppercase tracking-widest"
            />
            <Button
                type="button"
                data-testid="login-invite-code-apply"
                variant="outline"
                onClick={() => applyInvite(value)}
                className="h-11 rounded-xl border-[#F57C00] text-[#F57C00] hover:bg-[#F57C00] hover:text-white"
            >
                {t("apply")}
            </Button>
        </div>
    );
}

/** Step 1 of login: phone + optional name + invite code entry. */
const PHONE_RE = /^[6-9]\d{9}$/;

function PhoneField({ t, phone, setPhone }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-[#1F1F1F] text-[15px] font-medium">
                {t("mobileNumber")}
            </Label>
            <div className="flex items-stretch rounded-xl border border-[#E5E5E5] focus-within:border-[#7C3AED] overflow-hidden bg-white">
                <span className="inline-flex items-center px-3 bg-[#FAFAFA] text-[#1F1F1F] font-medium border-r border-[#E5E5E5]">
                    +91
                </span>
                <Input
                    data-testid="login-phone-input"
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    placeholder="98XXXXXXXX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1 h-12 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-[17px] tracking-wider bg-white"
                />
            </div>
            <p
                className={`text-xs ${
                    phone.length === 10 && !PHONE_RE.test(phone)
                        ? "text-red-600 font-medium"
                        : "text-[#777]"
                }`}
                data-testid="login-phone-hint"
            >
                {t("phoneHint")}
            </p>
        </div>
    );
}

function NameField({ t, name, setName }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor="name" className="text-[#1F1F1F] text-[15px] font-medium">
                {t("yourName")}{" "}
                <span className="text-[#777] font-normal">{t("firstTimeOnly")}</span>
            </Label>
            <Input
                data-testid="login-name-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aarav Sharma"
                className="h-12 bg-white border-[#E5E5E5] text-[16px]"
            />
        </div>
    );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailField({ email, setEmail }) {
    const invalid = email.trim().length > 0 && !EMAIL_RE.test(email.trim());
    return (
        <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[#1F1F1F] text-[15px] font-medium">
                Email <span className="text-[#777] font-normal">(optional)</span>
            </Label>
            <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 bg-white border-[#E5E5E5] text-[16px]"
            />
            <p className={`text-xs ${invalid ? "text-red-600 font-medium" : "text-[#777]"}`}>
                {invalid
                    ? "Please enter a valid email address."
                    : "For welcome mail & receipts — never spam."}
            </p>
        </div>
    );
}

export function PhoneStep({
    t,
    phone,
    setPhone,
    name,
    setName,
    email,
    setEmail,
    invitePreview,
    applyInvite,
    sending,
    onSubmit,
    showName = true,
    showEmail = false,
    submitLabelKey,
    requireConsent = false,
    captchaToken = "",
    setCaptchaToken,
    captchaResetSignal = 0,
    captchaRequired = true,
}) {
    const [consent, setConsent] = useState(false);
    const emailInvalid = showEmail && (email || "").trim().length > 0 && !EMAIL_RE.test((email || "").trim());
    const showCaptcha = !!RECAPTCHA_SITE_KEY && captchaRequired && !!setCaptchaToken;
    return (
        <form onSubmit={onSubmit} className="space-y-3">
            <PhoneField t={t} phone={phone} setPhone={setPhone} />
            {showName && <NameField t={t} name={name} setName={setName} />}
            {showEmail && <EmailField email={email || ""} setEmail={setEmail} />}

            {!invitePreview && applyInvite && <InviteCodeEntry t={t} applyInvite={applyInvite} />}

            {requireConsent && (
                <label
                    className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl bg-[#F7F9F4] border border-[#E1E8DB] px-3.5 py-2"
                    data-testid="signup-consent-label"
                >
                    <Checkbox
                        data-testid="signup-consent-checkbox"
                        checked={consent}
                        onCheckedChange={(v) => setConsent(v === true)}
                        className="mt-0.5 border-[#1B5E20]/50 data-[state=checked]:bg-[#1B5E20] data-[state=checked]:border-[#1B5E20]"
                    />
                    <span className="text-[12.5px] text-[#444] leading-snug">
                        I agree to the{" "}
                        <Link to="/copyright" className="text-[#1B5E20] underline hover:no-underline" onClick={(e) => e.stopPropagation()}>Terms of Service</Link>,{" "}
                        <Link to="/customer-consent" className="text-[#1B5E20] underline hover:no-underline" onClick={(e) => e.stopPropagation()}>Consent Policy</Link>{" "}&amp;{" "}
                        <Link to="/customer-awareness" className="text-[#1B5E20] underline hover:no-underline" onClick={(e) => e.stopPropagation()}>Awareness Disclaimer</Link>.
                    </span>
                </label>
            )}

            {showCaptcha && (
                <div className="pt-0.5" data-testid="phone-step-captcha">
                    <Recaptcha onChange={setCaptchaToken} resetSignal={captchaResetSignal} />
                </div>
            )}

            <Button
                type="submit"
                data-testid="login-send-otp-btn"
                disabled={sending || !PHONE_RE.test(phone) || emailInvalid || (requireConsent && !consent) || (showCaptcha && !captchaToken)}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white text-[16px] font-semibold shadow-md"
            >
                {sending ? (
                    t("sendingOtp")
                ) : (
                    <>
                        {t(submitLabelKey || "sendOtp")} <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                )}
            </Button>

            {!requireConsent && <p className="text-center text-xs text-[#777]">{t("terms")}</p>}
        </form>
    );
}
